import { useCallback } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getPriorityOpportunities } from "@/helpers/apis";
import { GET_PRIORITY_OPPORTUNITIES_DATA } from "../constants";
import type { PriorityOpportunityResponse } from "../types";
import { useExplorerFiltersFromUrl } from "./useExplorerFiltersFromUrl";
import type { OpportunitiesFilters } from "../schemas";

interface UsePriorityOpportunitiesQueryOptions {
  enabled?: boolean;
  filterParams?: OpportunitiesFilters;
}

export const usePriorityOpportunitiesQuery = (
  options: UsePriorityOpportunitiesQueryOptions = {}
) => {
  const { enabled = true, filterParams: externalFilterParams } = options;
  // Fall back to URL-derived params when the caller doesn't supply them
  // (e.g. in tests or legacy usage sites).
  const { filterParams: urlFilterParams } = useExplorerFiltersFromUrl();
  const filterParams = externalFilterParams ?? urlFilterParams;

  // The `/priority` endpoint returns the tenant's curated set across ALL
  // instances — it ignores the instance filter. So `instance_id` is
  // deliberately kept OUT of the query key / request params: switching the
  // selected instance must NOT trigger a refetch of identical data. The same
  // cached payload is reused and only re-sliced client-side (below), making
  // instance switching instantaneous.
  const { instance_id, ...queryFilterParams } = filterParams;

  // Slice the cached, tenant-wide payload to the selected instance using each
  // row's `instance_id` vs the selected `instance` URL param. This drives both
  // the list contents and the per-instance default-tab decision in
  // DiscoverContentV2, so an instance with no priority opps lands on the All
  // tab instead of an empty Priority page. No selection = all instances.
  // Wrapped in `useCallback` (keyed on the selection) so `select` isn't a fresh
  // reference every render — otherwise React Query re-runs it on each render.
  const selectByInstance = useCallback(
    (data: PriorityOpportunityResponse[] | undefined) => {
      if (!data) return data;
      if (!instance_id || instance_id.length === 0) return data;
      const selected = new Set(instance_id.map(String));
      return data.filter(o => selected.has(String(o.instance_id)));
    },
    [instance_id]
  );

  return useQuery({
    queryKey: [GET_PRIORITY_OPPORTUNITIES_DATA, queryFilterParams],
    queryFn: () => getPriorityOpportunities(queryFilterParams),
    enabled,
    // Keep the previous payload while a non-instance filter change (date range,
    // etc.) refetches, so `isLoading` only fires on the very first load. This
    // stops the default-tab loader gate in DiscoverContentV2 from unmounting
    // the tab strip on every subsequent filter change.
    placeholderData: keepPreviousData,
    select: selectByInstance,
  });
};
